/** Decode an owned image request without letting a removed face upload later. */
export function loadFaceImage(url: string, receive: (image: ImageBitmap) => void): () => void {
  const controller = new AbortController()
  let active = true
  void (async () => {
    const response = await fetch(url, { signal: controller.signal, mode: 'cors', credentials: 'same-origin' })
    if (!response.ok) throw new Error(`Artwork request failed: ${response.status}`)
    const blob = await response.blob()
    if (!active) return
    const image = await createImageBitmap(blob)
    try {
      // Bitmap decoding cannot be aborted. A release during decoding still closes
      // the decoded bitmap without ever handing it to the GPU resource owner.
      if (active) receive(image)
    } finally {
      image.close()
    }
  })().catch(() => {
    // A missing image, unsupported format, or cancellation leaves the generated
    // face intact. Retry only when a new face lifetime starts, never every frame.
  })
  return () => {
    active = false
    controller.abort()
  }
}

/** Preserve the physical print aspect, native detail, and a bounded GPU budget. */
export function imageTextureSize(width: number, height: number, aspect: number, limit = 2048): readonly [number, number] {
  if (![width, height, aspect, limit].every(value => Number.isFinite(value) && value > 0)) {
    throw new Error('Artwork dimensions must be positive finite numbers.')
  }
  const textureHeight = Math.min(height, width / aspect, limit, limit / aspect)
  return [Math.max(1, Math.round(textureHeight * aspect)), Math.max(1, Math.round(textureHeight))]
}

/** Full face artwork replaces generated print; metadata stays in the DOM inspector. */
export function createImageCanvas(image: ImageBitmap, aspect: number, background = '#f3edda', limit = 2048, rotation: 0 | 90 | 180 | 270 = 0): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  const sideways = rotation === 90 || rotation === 270
  const [width, height] = imageTextureSize(sideways ? image.height : image.width, sideways ? image.width : image.height, aspect, limit)
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas2D is required to print image artwork.')
  context.fillStyle = background
  context.fillRect(0, 0, width, height)
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  if (rotation) {
    context.translate(width / 2, height / 2)
    context.rotate(rotation * Math.PI / 180)
    context.drawImage(image, -(sideways ? height : width) / 2, -(sideways ? width : height) / 2, sideways ? height : width, sideways ? width : height)
  } else context.drawImage(image, 0, 0, width, height)
  return canvas
}

/** Allocate and upload all mip levels once; never regenerate artwork in a draw. */
export function createArtworkTexture(device: GPUDevice, canvas: HTMLCanvasElement | null): GPUTexture {
  const mipLevelCount = canvas ? Math.floor(Math.log2(Math.max(canvas.width, canvas.height))) + 1 : 1
  const texture = device.createTexture({
    label: 'Printed component artwork', size: [canvas?.width ?? 1, canvas?.height ?? 1],
    format: 'rgba8unorm-srgb', mipLevelCount,
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT,
  })
  try {
    if (!canvas) {
      device.queue.writeTexture({ texture }, new Uint8Array([255, 255, 255, 255]), { bytesPerRow: 4 }, [1, 1])
      return texture
    }
    device.queue.copyExternalImageToTexture({ source: canvas }, { texture }, [canvas.width, canvas.height])
    let previous = canvas
    for (let level = 1; level < mipLevelCount; level++) {
      const mip = document.createElement('canvas')
      mip.width = Math.max(1, Math.floor(canvas.width / 2 ** level))
      mip.height = Math.max(1, Math.floor(canvas.height / 2 ** level))
      const context = mip.getContext('2d')
      if (!context) throw new Error('Canvas2D is required to filter image artwork.')
      context.imageSmoothingEnabled = true
      context.imageSmoothingQuality = 'high'
      context.drawImage(previous, 0, 0, mip.width, mip.height)
      device.queue.copyExternalImageToTexture({ source: mip }, { texture, mipLevel: level }, [mip.width, mip.height])
      previous = mip
    }
    return texture
  } catch (error) {
    texture.destroy()
    throw error
  }
}
